/**
 * Watchdog - names what the page had just started when it froze.
 *
 * WHY (2026-10-08). The black box proves WHEN the page stopped running
 * (heartbeats end, the next run starts minutes later), and it caught the
 * 2026-10-07 pre-gig freeze exactly: 16:27:47 to 16:35:03, screen frozen, no
 * touch response, socket to the Mac open throughout. It cannot say WHERE. A
 * WebKit soak of the operator's own page did not reproduce it, and reading
 * the code did not find it, so the third gig freeze still had no cause.
 *
 * HOW. Every labelled job writes one line to localStorage as it STARTS
 * (`watchJob`), and a 1s heartbeat writes the time. A freeze is synchronous -
 * nothing can hang inside an `await` - so the page dies part-way through the
 * last thing it started. Each job is saved OPEN as it starts and marked done
 * once its task returns (see `JobEntry.open`), so after the force-quit the
 * job still open is the one the page froze inside.
 *
 * WHY NOT A WORKER, which was the first build and is worth not repeating.
 * A worker keeps running while the page is stuck, so it looked like the way
 * to tell "page stuck" from "whole app stopped". Measured in WebKit
 * (Playwright 1.60, persistent profile, main thread spun for 60s, force-quit
 * at +10s): the worker's IndexedDB writes stopped 0.0s and 0.5s into the
 * freeze on two runs, the same as a normal close. WebKit routes a worker's
 * storage through the page's main thread, so the worker sees the freeze and
 * cannot write it down. A localStorage write made immediately BEFORE the
 * freeze, by contrast, survived the force-quit (with a control written after
 * the spin correctly absent) - which is also why the black box's own last
 * heartbeat survives. So the record is kept on the main thread, written
 * before the work rather than about it.
 *
 * WHAT IT CANNOT DO. It names the last JOB started, not the line. Work that
 * is not labelled (drawing the screen, an unlabelled handler, iOS stopping
 * the app) shows as "nothing started since the last heartbeat", which is a
 * finding too: it points away from the labelled jobs. A job label means
 * "where to look first", not "the culprit". It also cannot tell an iOS stop
 * from a stuck page; the black box's gap plus this line is what there is.
 *
 * COST. Two small localStorage writes per labelled job (start, done) and one
 * per second, each a few hundred bytes; idle polls with no bridge are not
 * labelled. Every storage touch is wrapped: a recorder that
 * throws would be a diagnostic that causes incidents. Touches no dispatch,
 * poll or reconnect path - the labels are one call at the top of each.
 */

const KEY_LIVE = 'opbuttons.watchdog.live.v1';
const KEY_PREVIOUS = 'opbuttons.watchdog.previous.v1';
const BEAT_MS = 1000;
/** A heartbeat this late means the page stalled and came back by itself. The
 *  timer fires every 1000ms; a busy moment delays one beat, not four. */
const STALL_MS = 4000;
const MAX_JOBS = 12;
const MAX_STALLS = 20;

/** `open` = started and its synchronous work has not returned. It is cleared
 *  by a zero-delay timer, which can only fire once the task that started the
 *  job - and every effect it set off synchronously - has finished. A freeze
 *  inside the job never lets it fire, so the job is saved still open. */
interface JobEntry { name: string; at: number; n: number; open?: boolean }
interface Stall { from: number; ms: number; jobs: JobEntry[] }
export interface WatchdogRecord {
  startedAt: number;
  /** Wall clock of the last heartbeat. */
  beatAt: number;
  beats: number;
  /** Newest last. */
  jobs: JobEntry[];
  /** Stalls the page came back from by itself, in this run. */
  stalls: Stall[];
}

let started = false;
let live: WatchdogRecord | null = null;
let previous: WatchdogRecord | null = null;

function save(): void {
  if (!live) return;
  try { localStorage.setItem(KEY_LIVE, JSON.stringify(live)); } catch { /* quota, private mode */ }
}

/** Name the job the page is starting. Written BEFORE the work, so a freeze
 *  inside it leaves this as the last word. Safe before start (dropped). */
export function watchJob(name: string): void {
  if (!live) return;
  const now = Date.now();
  let entry = live.jobs[live.jobs.length - 1];
  // A job that repeats back to back (the clock, several times a second)
  // updates its own row rather than pushing every other name out.
  if (entry && entry.name === name) { entry.at = now; entry.n += 1; entry.open = true; }
  else {
    entry = { name: name.slice(0, 60), at: now, n: 1, open: true };
    live.jobs.push(entry);
    if (live.jobs.length > MAX_JOBS) live.jobs.splice(0, live.jobs.length - MAX_JOBS);
  }
  save();
  // The close is SAVED too, not just set in memory. Measured: closing in
  // memory only left the last finished job saved as open whenever the page
  // stopped before the next write, so a normal close and a freeze in
  // unlabelled work both blamed an innocent clock poll. `n` guards the
  // repeat case: an earlier run's timer must not close the current one.
  const done = entry;
  const run = done.n;
  setTimeout(() => {
    if (done.n !== run || !done.open) return;
    done.open = false;
    save();
  }, 0);
}

/** Start recording. Idempotent. Keeps the run that just ended first: the
 *  force-quit that ends a freeze is followed at once by this boot. */
export function startWatchdog(): void {
  if (started) return;
  started = true;
  try {
    const raw = localStorage.getItem(KEY_LIVE);
    if (raw) {
      localStorage.setItem(KEY_PREVIOUS, raw);
      previous = JSON.parse(raw) as WatchdogRecord;
    } else {
      const p = localStorage.getItem(KEY_PREVIOUS);
      previous = p ? (JSON.parse(p) as WatchdogRecord) : null;
    }
  } catch { previous = null; }
  const now = Date.now();
  live = { startedAt: now, beatAt: now, beats: 0, jobs: [], stalls: [] };
  save();
  setInterval(() => {
    if (!live) return;
    const t = Date.now();
    const gap = t - live.beatAt;
    if (gap >= STALL_MS) {
      // The page was stuck and came back. Kept, because a six-second hang
      // mid-song is worth knowing about even when nobody restarts anything.
      const from = live.beatAt;
      live.stalls.push({ from, ms: gap, jobs: live.jobs.filter((j) => j.at >= from - BEAT_MS) });
      if (live.stalls.length > MAX_STALLS) live.stalls.splice(0, live.stalls.length - MAX_STALLS);
    }
    live.beatAt = t;
    live.beats += 1;
    save();
  }, BEAT_MS);
  // A tap is a job too: if pressing a control is what sets a freeze off, the
  // record should say which control. Capture phase, passive, never stops it.
  try {
    window.addEventListener('pointerdown', (ev) => {
      const el = ev.target instanceof Element ? ev.target.closest('[data-kind]') : null;
      watchJob(`tap:${el?.getAttribute('data-kind') ?? 'chrome'}`);
    }, { capture: true, passive: true });
  } catch { /* no window */ }
}

/** The previous run's record, or null when there is none yet. */
export function readWatchdogPrevious(): WatchdogRecord | null {
  return previous;
}

const time = (t: number): string => new Date(t).toLocaleTimeString();
const secs = (ms: number): string => (ms >= 120_000 ? `${Math.round(ms / 60000)} min` : `${Math.round(ms / 1000)}s`);
const jobList = (jobs: JobEntry[]): string =>
  jobs.slice().reverse().map((j) => `${j.name} (${time(j.at)}${j.n > 1 ? `, x${j.n}` : ''}${j.open ? ', never returned' : ''})`).join(', ');

/**
 * The previous run's last moments in one sentence: what had started after the
 * final heartbeat. Only meaningful next to the black box's JS STOPPED gap - a
 * normal close also ends a run - which is where `blackBoxVerdict` puts it.
 */
export function watchdogVerdict(): string {
  const p = previous;
  if (!p) return 'Watchdog: no previous run recorded on this device yet.';
  const open = p.jobs.filter((j) => j.open);
  const recovered = p.stalls.length > 0
    ? ` Earlier in that run the page also stalled ${p.stalls.length} time${p.stalls.length === 1 ? '' : 's'} and recovered by itself (longest ${secs(Math.max(...p.stalls.map((s) => s.ms)))}).`
    : '';
  const before = p.jobs.filter((j) => !j.open).slice(-3);
  const context = ` Last jobs that finished: ${before.length ? jobList(before) : 'none'}.`;
  if (open.length > 0) {
    return `Watchdog: the page stopped INSIDE ${jobList(open)} - it started and never returned. `
      + `That is where to look first.${context}${recovered}`;
  }
  return `Watchdog: no labelled job was running when the page stopped (last heartbeat ${time(p.beatAt)}), `
    + `so it stopped while idle or in unlabelled work (drawing the screen, or iOS stopping the app).`
    + `${context}${recovered}`;
}

/** Everything the watchdog knows about the previous run, for the report text. */
export function exportWatchdog(): string {
  const p = previous;
  const head = '\n--- watchdog (previous run) ---\n';
  if (!p) return `${head}${watchdogVerdict()}\n`;
  return head
    + `${watchdogVerdict()}\n`
    + `started ${new Date(p.startedAt).toISOString()}, last heartbeat ${new Date(p.beatAt).toISOString()} (${p.beats} beats)\n`
    + `last jobs, newest first: ${p.jobs.length ? jobList(p.jobs) : 'none'}\n`
    + `stalls the page recovered from (from,length,jobs): ${p.stalls.length === 0 ? 'none' : ''}\n`
    + p.stalls.map((s) => `${new Date(s.from).toISOString()},${secs(s.ms)},${s.jobs.length ? jobList(s.jobs) : 'none labelled'}`).join('\n')
    + '\n';
}
